import React, { Suspense, useEffect, useState } from "react";
import {
  Box,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { isMobile } from "react-device-detect";
import AppButton from "@/features/line-sheet-sets/app-button";
import { Form, Formik } from "formik";
import useIdentity from "@/features/ui/hooks/use-identity";
import SearchInput from "@/shared/components/search-input";
import AddedSkuList from "@/features/my-account/added-sku-list";
import SearchedSkuList from "@/features/my-account/searched-sku-list";
import useUpdateFavSkus from "@/features/my-account/hooks/useUpdateFavSkus";
import useGetFavSkus from "@/features/my-account/hooks/useGetFavSkus";
import NoResults from "@/shared/components/no-results";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

function FavoriteProductEditModal({ isOpen, onClose }: Props) {
  const [value, setValue] = React.useState<string>("");
  const identity = useIdentity();
  const [isEnterPressed, setIsEnterPressed] = useState(false);
  const buyerId = identity?.company?.id;
  const { mutateFavSkus } = useUpdateFavSkus(buyerId);
  const { favSkus } = useGetFavSkus(buyerId);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        favoriteProducts: favSkus.map((item) => ({
          ...item,
          added: true, // 추가된 상품 표시
        })),
        requestSku: [],
        favProdsSubmit: [],
        requestSkuSubmit: [],
      }}
      onSubmit={(values) => {
        // const favSkusInPathrade = favSkus
        //   .filter((item) => item.sku_in_pathrade)
        //   .map((item) => item.id);
        // const favSkusNotInPathrade = favSkus
        //   .filter((item) => !item.sku_in_pathrade)
        //   .map((item) => item.id);
        // console.log(favSkusInPathrade, values.favoriteProducts);
        mutateFavSkus({
          skus: values.favoriteProducts
            .filter((prod) => !prod.sku_in_pathrade && prod.added)
            .map((item) => item.sku),
          sku_ids: values.favoriteProducts
            .filter((prod) => prod.sku_in_pathrade && prod.added)
            .map((item) => item.id),
        });
        onClose();
      }}
    >
      {({ values, initialValues, dirty }) => {
        // 초기값과 현재 favoriteProducts 비교
        const isFavoriteDirty =
          JSON.stringify(values.favoriteProducts) !==
          JSON.stringify(initialValues.favoriteProducts);

        return (
          // allowPinchZoom: 검색 입력에서 화면이 확대됐을 때 두 손가락 축소로
          // 빠져나올 수 있게 스크롤 락의 핀치 제스처 차단을 푼다.
          <Modal
            isOpen={isOpen}
            onClose={onClose}
            closeOnOverlayClick={false}
            allowPinchZoom={isMobile}
          >
            <ModalOverlay />
            <ModalContent borderRadius={"8px"}>
              <Form>
                <ModalHeader background={"#F7F9FA"} padding={"12px 16px"}>
                  <Text textStyle={"body-md-bold"} color={"#444444"}>
                    관심 상품 관리
                  </Text>
                </ModalHeader>
                <ModalBody display="flex" flexDirection={"column"} gap={"16px"}>
                  <Text textStyle={"body-md-regular"} color={"#6F6F6C"}>
                    관심 상품을 검색해서 추가하거나 삭제할 수 있어요.{" "}
                  </Text>
                  <SearchInput
                    value={value}
                    placeholder={"SKU를 검색해 보세요."}
                    onChange={(txt) => {
                      setValue(txt);
                      setIsEnterPressed(false);
                    }}
                    onKeyDown={() => {
                      if (value.trim() === "") return;
                      setIsEnterPressed(true);
                    }}
                  />
                  <Suspense
                    fallback={
                      // 400px 고정이면 모바일 뷰포트를 넘겨 로딩 중 모달이 가로로 터진다.
                      // 아래 목록과 같은 높이(358px)를 유지해 로딩 후 높이가 튀지 않게 한다.
                      <Flex
                        width={"100%"}
                        height={"358px"}
                        justifyContent={"center"}
                        alignItems={"center"}
                      >
                        <Spinner />
                      </Flex>
                    }
                  >
                    {buyerId &&
                      (isEnterPressed ? (
                        <SearchedSkuList buyer_id={buyerId} value={value} />
                      ) : (
                        <AddedSkuList favSkus={favSkus} />
                      ))}
                  </Suspense>
                </ModalBody>
                <ModalFooter
                  display={"flex"}
                  justifyContent={"space-between"}
                  gap={"12px"}
                  paddingLeft={"16px"}
                >
                  <Box
                    display="flex"
                    gap="12px"
                    width={"100%"}
                    justifyContent={"end"}
                  >
                    <AppButton variant={"normal"} onClick={() => onClose()}>
                      <Text textStyle={"body-sm-bold"} color={"#444444"}>
                        취소
                      </Text>
                    </AppButton>
                    <AppButton
                      variant={"primary"}
                      type={"submit"}
                      isDisabled={!dirty}
                    >
                      <Text textStyle={"body-sm-bold"} color={"#FFFFFF"}>
                        저장
                      </Text>
                    </AppButton>
                  </Box>
                </ModalFooter>
              </Form>
            </ModalContent>
          </Modal>
        );
      }}
    </Formik>
  );
}

export default FavoriteProductEditModal;
